"use client";

import { METADATA_CERTIFICATE } from "@constants/global";
import { CertificatePublicPageV2 } from "components/ui/v2/certificates";
import { gqlCertificateUser } from "gql";
import { IPublicUserCertificateDetail } from "gql/Certificate-user/queries";
import { GetServerSideProps } from "next";
import useTranslation from "next-translate/useTranslation";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import apolloClient from "settings/apollo";

interface IPublicUserCertificateProps {
  publicUserResponse: IPublicUserCertificateDetail | null;
  host: string;
}

const Certificate = (props: IPublicUserCertificateProps) => {
  const { t } = useTranslation("common");
  const { publicUserResponse, host } = props;
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsClient(true);
    if (!publicUserResponse) {
      void router.push("/404");
    }
  }, [publicUserResponse, router]);

  if (!publicUserResponse && !isClient) return null;
  if (!publicUserResponse) return null;

  const certificateUrl = `https://${host}/certificate/${publicUserResponse.certificateUserId}`;
  const learningDescription = publicUserResponse.learningOutcomes.join(", ");

  return (
    <>
      <Head>
        <title>
          {t("certificacion_meta", {
            name: `${publicUserResponse.userName} ${publicUserResponse.userLastName || ""}`,
          })}{" "}
          | Groowcity
        </title>
        <meta
          key="certificate-title"
          property="title"
          content={`${publicUserResponse.certificateName} | Groowcity`}
        />
        <meta name="author" content="Groowcity" />
        <meta
          key="certificate-description"
          property="description"
          content={learningDescription}
        />
        <meta key="certificate-url" property="url" content={certificateUrl} />
        <meta
          key="certificate-image"
          property="image"
          content={METADATA_CERTIFICATE}
        />
        <meta key="certificate-og-type" property="og:type" content="website" />
        <meta key="certificate-og-url" property="og:url" content={certificateUrl} />
        <meta
          key="certificate-og-title"
          property="og:title"
          content={`${publicUserResponse.certificateName} | Groowcity`}
        />
        <meta
          key="certificate-og-description"
          property="og:description"
          content={learningDescription}
        />
        <meta
          key="certificate-og-image"
          property="og:image"
          content={METADATA_CERTIFICATE}
        />
        <meta
          key="certificate-og-image-width"
          property="og:image:width"
          content="1200"
        />
        <meta
          key="certificate-og-image-height"
          property="og:image:height"
          content="650"
        />
        <meta
          key="certificate-twitter-image"
          property="twitter:image"
          content={METADATA_CERTIFICATE}
        />
        <meta
          key="certificate-twitter-card"
          property="twitter:card"
          content="summary_large_image"
        />
        <meta
          key="certificate-twitter-url"
          property="twitter:url"
          content={certificateUrl}
        />
        <meta
          key="certificate-twitter-title"
          property="twitter:title"
          content={`${publicUserResponse.userName} ${publicUserResponse.userLastName} | Groowcity`}
        />
        <meta
          key="certificate-twitter-description"
          property="twitter:description"
          content={`${publicUserResponse.certificateSubtitle}`}
        />
      </Head>
      {isClient ? <CertificatePublicPageV2 data={publicUserResponse} /> : null}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  let publicUserResponse: IPublicUserCertificateDetail | null = null;
  const { req, query } = context;
  const host = req.headers.host ?? "";

  try {
    const { data } = await apolloClient.query<{
      publicUserCertificate: IPublicUserCertificateDetail;
    }>({
      query: gqlCertificateUser.queries.PUBLIC_USER_CERTIFICATE,
      variables: { userCertificateId: query.id },
      fetchPolicy: "network-only",
    });

    publicUserResponse = data?.publicUserCertificate ?? null;

    return {
      props: {
        publicUserResponse,
        host,
      },
    };
  } catch (error) {
    console.error("🚀 ~ error", error);
  }

  return {
    props: {
      publicUserResponse: null,
      host,
    },
  };
};

export default Certificate;
